import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import arrayMove from 'array-move';
import Sortable from '../../Sortable';
import AudioHandler from './AudioHandler';
import Input from '../../Input';
import produce from 'immer';
import { ReactComponent as Delete } from '../../../assets/svg/delete.svg';
import { ReactComponent as Edit } from '../../../assets/svg/edit.svg';
import { ReactComponent as Add } from '../../../assets/svg/add.svg';
import { useAudios } from '../../../hooks/useAudios';
import AudioPlay from '../../AudioPlay';
import Modal from '../../Modal';
import Button from '../../Button';
import clean from 'clean-deep';

const LessonEditDataDiscoverSentence = ({ data = [], onChange = () => {} }) => {
  const [state, setState] = useState([]);

  const [extractedAudios, setExtractedAudios] = useState([]);

  const audios = useAudios(extractedAudios);

  const [showItemModal, setShowItemModal] = useState(undefined);
  const [itemText, setItemText] = useState('');
  const [itemAudio, setItemAudio] = useState(undefined);

  useEffect(() => {
    const state = formatState(data);
    setState(state);
    setExtractedAudios(state.map((i) => i.audio));
  }, [data]);

  useEffect(() => {
    onChange({
      data: clean(translateState(state)),
    });
  }, [state, onChange]);

  const isLoaded = ({ audio }) => (audio && audios[audio]) || !audio;

  const onSortEnd = ({ oldIndex, newIndex }) => setState((state) => arrayMove(state, oldIndex, newIndex));

  const onDeleteItem = (index) =>
    setState(
      produce((state) => {
        state.splice(index, 1);
      })
    );

  const onAddItem = () => {
    setItemAudio(undefined);
    setItemText('');
    setShowItemModal({
      submit: 'Agregar',
      onSubmit: ({ text, audio }) => {
        setExtractedAudios((state) => [...state, audio]);
        setState(
          produce((state) => {
            state.push({
              text: text,
              audio,
            });
          })
        );
        setShowItemModal(undefined);
      },
    });
  };

  const onEditItem = (index) => {
    setItemAudio(state[index].audio);
    setItemText(state[index].text);
    setShowItemModal({
      submit: 'Guardar',
      onSubmit: ({ text, audio }) => {
        setExtractedAudios((state) => [...state, audio]);
        setState(
          produce((state) => {
            state[index].text = text;
            state[index].audio = audio;
          })
        );
        setShowItemModal(undefined);
      },
    });
  };

  return (
    <Container>
      {showItemModal && (
        <ModalStyled>
          <ModalContainer>
            <ModalBody>
              <Handlers>
                <AudioHandler audio={itemAudio} onChange={(obj) => setItemAudio(obj.name)} />
              </Handlers>
              <Input placeholder='Texto' value={itemText} onChange={(e) => setItemText(e.target.value)} />
            </ModalBody>
            <ModalButtons>
              <Button secondary text='Cancelar' onClick={() => setShowItemModal(undefined)} />
              <Button
                primary
                text={showItemModal.submit}
                onClick={() =>
                  showItemModal.onSubmit({
                    text: itemText,
                    audio: itemAudio,
                  })
                }
              />
            </ModalButtons>
          </ModalContainer>
        </ModalStyled>
      )}
      <Sortable
        items={state}
        render={(item, index) => (
          <Wrapper>
            {isLoaded(item) ? (
              <>
                <Detail>
                  {item.audio && audios[item.audio] && <AudioPlay audio={audios[item.audio]} />}
                  {item.text && <Text>{item.text}</Text>}
                </Detail>
                <Options>
                  <OptionIcon as={Delete} color='#c62828' onClick={() => onDeleteItem(index)} />
                  <OptionIcon as={Edit} color='#1976d2' onClick={() => onEditItem(index)} />
                </Options>
              </>
            ) : (
              <Spinner src='loading.gif' />
            )}
          </Wrapper>
        )}
        onSortEnd={onSortEnd}
      />
      <WrapperAdd onClick={onAddItem}>
        <OptionIcon as={Add} color='#2196f3' />
      </WrapperAdd>
    </Container>
  );
};

const formatState = (state) =>
  state.map((state) => {
    const res = { ...state, text: (state.words || []).join(' ') };
    return res;
  });

const translateState = (state) =>
  state.map((item) => ({
    audio: item.audio,
    words: (item.text || '').split(' '),
  }));

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  padding: 15px 15px;
  border-radius: 10px;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  user-select: none;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  background-color: #caedff;
  margin-bottom: 10px;
`;

const WrapperAdd = styled.div`
  border-radius: 10px;
  border: 2px dashed #2196f3;
  min-height: 50px;
  width: 100%;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
`;

const Detail = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  & > * {
    margin: 5px;
  }
`;

const Handlers = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  & > * {
    margin: 5px;
  }
`;

const Text = styled.div`
  font-size: 0.9em;
`;

const Options = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionIcon = styled.div`
  padding: 5px;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  cursor: pointer;
  fill: ${(props) => props.color || 'black'};
  :not(:last-child) {
    margin-right: 10px;
  }
`;

const Spinner = styled.img`
  margin: auto;
  width: 25px;
  height: 25px;
  display: block;
`;

const ModalStyled = styled(Modal)`
  max-width: 400px;
  width: 100%;
`;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;

  & > *:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const ModalBody = styled.div`
  & > *:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  & > * {
    margin: 5px;
  }

  @media (max-width: 240px) {
    flex-wrap: wrap;
    flex-direction: column-reverse;
    justify-content: center;
  }
`;

export default LessonEditDataDiscoverSentence;
